import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Mail, Lock, User, Loader2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LanguageSelector } from '@/components/outfit/LanguageSelector';
import { SponsorBadge } from '@/components/SponsorBadge';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, register, isLoading, error, clearError } = useAuthStore();

  const [isRegistering, setIsRegistering] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
  });
  const [passwordMismatch, setPasswordMismatch] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setPasswordMismatch(false);

    if (isRegistering) {
      if (formData.password !== formData.confirmPassword) {
        setPasswordMismatch(true);
        return;
      }
      const success = await register(formData.email, formData.password, formData.name);
      if (success) navigate('/events');
    } else {
      const success = await login(formData.email, formData.password);
      if (success) navigate('/events');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (e.target.name === 'confirmPassword' || e.target.name === 'password') {
      setPasswordMismatch(false);
    }
  };

  const switchMode = () => {
    setIsRegistering(!isRegistering);
    clearError();
    setPasswordMismatch(false);
  };

  return (
    <div className="min-h-dvh bg-background flex flex-col relative overflow-hidden">
      {/* Decorative blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-[15%] -left-[10%] w-[55vw] h-[55vw] max-w-[30rem] max-h-[30rem] rounded-full bg-primary/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-[15%] -right-[10%] w-[55vw] h-[55vw] max-w-[30rem] max-h-[30rem] rounded-full bg-accent/20 blur-3xl"
      />

      {/* Header */}
      <header className="flex items-center justify-between px-5 py-4 relative z-10">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 hover:opacity-70 transition-opacity"
        >
          <ArrowLeft className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
          <Sparkles className="w-4 h-4 text-primary" aria-hidden="true" />
          <span className="font-serif italic text-lg font-bold text-foreground tracking-wide">
            {t('app.title')}
          </span>
        </button>
        <div className="bg-white/60 backdrop-blur-md rounded-full px-2 py-1 border border-border">
          <LanguageSelector />
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-4 py-6 relative z-10">
        <div
          className="w-full animate-spring-in"
          style={{ maxWidth: 'min(26rem, 92vw)' }}
        >
          <div className="bg-white/80 backdrop-blur-xl border border-border shadow-2xl shadow-primary/8 rounded-3xl p-7 sm:p-9">
            {/* Logo + title */}
            <div className="text-center mb-7">
              <div className="w-14 h-14 bg-gradient-to-br from-primary to-primary/80 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-primary/25">
                <Sparkles className="w-7 h-7 text-white" aria-hidden="true" />
              </div>
              <h1 className="font-serif italic text-3xl font-bold text-foreground mb-1 tracking-tight">
                {isRegistering ? t('auth.createAccount') : t('auth.welcomeBack')}
              </h1>
              <p className="text-muted-foreground text-sm font-medium">{t('app.subtitle')}</p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {error && (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {isRegistering && (
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-sm font-semibold text-foreground ml-1">
                    {t('auth.name')}
                  </Label>
                  <div className="relative group">
                    <User
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground transition-colors group-focus-within:text-primary"
                      aria-hidden="true"
                    />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder={t('auth.name')}
                      autoComplete="name"
                      className="pl-11 h-12 rounded-xl border-border bg-white/50 focus:bg-white focus-visible:ring-primary/30 focus-visible:border-primary transition-all"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-semibold text-foreground ml-1">
                  {t('auth.email')}
                </Label>
                <div className="relative group">
                  <Mail
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground transition-colors group-focus-within:text-primary"
                    aria-hidden="true"
                  />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@example.com"
                    autoComplete="email"
                    required
                    className="pl-11 h-12 rounded-xl border-border bg-white/50 focus:bg-white focus-visible:ring-primary/30 focus-visible:border-primary transition-all"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm font-semibold text-foreground ml-1">
                  {t('auth.password')}
                </Label>
                <div className="relative group">
                  <Lock
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground transition-colors group-focus-within:text-primary"
                    aria-hidden="true"
                  />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    autoComplete={isRegistering ? 'new-password' : 'current-password'}
                    required
                    minLength={6}
                    className="pl-11 pr-11 h-12 rounded-xl border-border bg-white/50 focus:bg-white focus-visible:ring-primary/30 focus-visible:border-primary transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors rounded-md focus-visible:ring-2 focus-visible:ring-primary/50"
                    aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {isRegistering && (
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" className="text-sm font-semibold text-foreground ml-1">
                    {t('auth.confirmPassword') || 'Conferma Password'}
                  </Label>
                  <div className="relative group">
                    <Lock
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground transition-colors group-focus-within:text-primary"
                      aria-hidden="true"
                    />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      minLength={6}
                      aria-describedby={passwordMismatch ? 'pw-mismatch' : undefined}
                      aria-invalid={passwordMismatch}
                      className={`pl-11 pr-11 h-12 rounded-xl border-border bg-white/50 focus:bg-white focus-visible:ring-primary/30 focus-visible:border-primary transition-all ${passwordMismatch ? 'border-destructive focus-visible:ring-destructive/30' : ''}`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground transition-colors rounded-md focus-visible:ring-2 focus-visible:ring-primary/50"
                      aria-label={showConfirmPassword ? 'Nascondi password' : 'Mostra password'}
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordMismatch && (
                    <p id="pw-mismatch" role="alert" className="text-xs text-destructive ml-1 mt-1">
                      {t('auth.passwordMismatch') || 'Le password non coincidono'}
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full h-12 rounded-full bg-gradient-to-r from-[#E91E8C] to-primary hover:opacity-90 text-white font-bold tracking-wide shadow-lg shadow-primary/25 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-primary/30 active:scale-95 active:translate-y-0 mt-2"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
                ) : isRegistering ? (
                  t('auth.register')
                ) : (
                  t('auth.login')
                )}
              </Button>
            </form>

            {/* Toggle mode */}
            <div className="mt-6 text-center">
              <p className="text-muted-foreground text-sm">
                {isRegistering ? t('auth.hasAccount') : t('auth.noAccount')}{' '}
                <button
                  type="button"
                  onClick={switchMode}
                  className="text-primary hover:text-primary/80 font-bold underline underline-offset-4 decoration-primary/40 transition-colors focus-visible:ring-2 focus-visible:ring-primary/50 rounded"
                >
                  {isRegistering ? t('auth.loginNow') : t('auth.registerNow')}
                </button>
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 space-y-1 pb-4">
        <SponsorBadge variant="footer" />
        <p className="text-center text-xs font-medium text-muted-foreground/40 font-serif italic">
          © {new Date().getFullYear()} Cannes Outfit Planner
        </p>
      </footer>
    </div>
  );
};
